/**
 * User Activity Tracking Service
 * Records last active time to Firestore with throttling
 * Also promotes user status from 'new' -> 'active' on first activity
 */
import { serverTimestamp } from 'firebase/firestore'
import { updateDocument, getDocument } from '../firebase/firestore.js'
import { version as appVersion } from '../../../package.json'
import logger from './logger.js'

const log = logger.createScoped('ActivityService')

// Throttle duration: 60 minutes in milliseconds
const THROTTLE_MS = 60 * 60 * 1000

// Heartbeat interval: 5 minutes in milliseconds
const HEARTBEAT_MS = 5 * 60 * 1000

// localStorage key prefixes
const STORAGE_KEY_PREFIX = 'gofood:lastActiveWriteAt:'
const PROMOTION_KEY_PREFIX = 'gofood:userStatusPromoted:'

/**
 * Get the localStorage key for a user
 * @param {string} uid
 * @returns {string}
 */
function getStorageKey(uid) {
  return `${STORAGE_KEY_PREFIX}${uid}`
}

/**
 * Check if we should write activity (throttle check)
 * @param {string} uid
 * @param {number} lastWriteMemory - In-memory last write timestamp
 * @returns {boolean}
 */
function shouldWriteActivity(uid, lastWriteMemory) {
  const now = Date.now()

  // Check in-memory first
  if (lastWriteMemory && now - lastWriteMemory < THROTTLE_MS) {
    return false
  }

  // Check localStorage
  try {
    const stored = localStorage.getItem(getStorageKey(uid))
    if (stored) {
      const storedMs = parseInt(stored, 10)
      if (!isNaN(storedMs) && now - storedMs < THROTTLE_MS) {
        return false
      }
    }
  } catch (e) {
    // localStorage may be unavailable
    log.warn('localStorage unavailable for throttle check', e)
  }

  return true
}

/**
 * Record the write timestamp in memory and localStorage
 * @param {string} uid
 * @returns {number} The recorded timestamp
 */
function recordWriteTimestamp(uid) {
  const now = Date.now()

  try {
    localStorage.setItem(getStorageKey(uid), String(now))
  } catch (e) {
    log.warn('Failed to store throttle timestamp in localStorage', e)
  }

  return now
}

/**
 * Get the localStorage key for status promotion flag
 * @param {string} uid
 * @returns {string}
 */
function getPromotionKey(uid) {
  return `${PROMOTION_KEY_PREFIX}${uid}`
}

/**
 * Check if status promotion has already been done (optimization only)
 * @param {string} uid
 * @returns {boolean}
 */
function hasPromotedStatus(uid) {
  try {
    return localStorage.getItem(getPromotionKey(uid)) === '1'
  } catch (e) {
    return false
  }
}

/**
 * Mark status promotion as done in localStorage
 * @param {string} uid
 */
function markStatusPromoted(uid) {
  try {
    localStorage.setItem(getPromotionKey(uid), '1')
  } catch (e) {
    log.warn('Failed to store promotion flag in localStorage', e)
  }
}

/**
 * Write activity to Firestore
 * @param {string} uid
 * @param {Object} options
 * @param {boolean} options.promoteStatus - Whether to include status promotion
 * @returns {Promise<{success: boolean, promoted: boolean}>}
 */
async function writeActivity(uid, { promoteStatus = false } = {}) {
  const payload = {
    last_active_at: serverTimestamp(),
    last_active_userAgent: navigator.userAgent || null,
    last_active_platform: navigator.platform || 'unknown',
    last_active_source: 'web',
    last_app_version: appVersion,
  }

  // Include status promotion in the same write if requested
  if (promoteStatus) {
    payload.status = 'active'
    payload.status_updated_at = serverTimestamp()
  }

  try {
    await updateDocument('Users', uid, payload)
    log.debug('Activity written to Firestore', { uid, promoted: promoteStatus })
    return { success: true, promoted: promoteStatus }
  } catch (err) {
    log.error('Failed to write activity', err)
    return { success: false, promoted: false }
  }
}

/**
 * Start user activity tracking
 * Writes activity on start and sets up heartbeat
 * Promotes status from 'new' -> 'active' on first activity write
 *
 * @param {Object} options
 * @param {string} options.uid - User ID
 * @param {Function} options.getProfile - Function that returns current profile (to check status)
 * @returns {Function} Stop function to cleanup
 */
export function startUserActivityTracking({ uid, getProfile }) {
  if (!uid) {
    log.warn('Cannot start activity tracking without uid')
    return () => {}
  }

  log.info('Starting activity tracking', { uid })

  // In-memory last write timestamp
  let lastWriteMs = 0
  let heartbeatTimer = null
  let stopped = false
  // In-memory flag to track if we've already handled promotion this session
  let promotionCheckedThisSession = false

  /**
   * Check if user needs status promotion (new -> active)
   * Returns true if status should be promoted
   * @returns {Promise<boolean>}
   */
  async function shouldPromoteStatus() {
    // Optimization: if already promoted (localStorage), skip
    if (hasPromotedStatus(uid)) {
      log.debug('Status already promoted (localStorage flag)', { uid })
      return false
    }

    // Already checked this session, no need to re-fetch
    if (promotionCheckedThisSession) {
      return false
    }

    // Fast-path: check local profile first
    const localProfile = getProfile?.()
    if (localProfile?.status && localProfile.status !== 'new') {
      // Status is not 'new', no promotion needed
      promotionCheckedThisSession = true
      // If status is active/inactive, mark as promoted to skip future checks
      if (localProfile.status === 'active' || localProfile.status === 'inactive') {
        markStatusPromoted(uid)
      }
      return false
    }

    // Fetch fresh profile from Firestore to ensure correctness
    try {
      const freshProfile = await getDocument('Users', uid)
      promotionCheckedThisSession = true

      if (!freshProfile) {
        log.debug('No profile found in Firestore', { uid })
        return false
      }

      if (freshProfile.status === 'deleted') {
        log.debug('User is deleted, skipping promotion', { uid })
        return false
      }

      if (freshProfile.status === 'new') {
        log.info('User status is "new", will promote to "active"', { uid })
        return true
      }

      // Status is not 'new', mark as already promoted
      markStatusPromoted(uid)
      return false
    } catch (err) {
      log.error('Failed to fetch profile for status check', err)
      return false
    }
  }

  /**
   * Attempt to write activity if not throttled
   */
  async function attemptWrite() {
    if (stopped) return

    // Check if user is deleted (from local state)
    const profile = getProfile?.()
    if (profile?.status === 'deleted') {
      log.debug('Skipping activity write for deleted user', { uid })
      return
    }

    // Force write if user has no version recorded yet
    const needsVersion = !profile?.last_app_version || profile.last_app_version !== appVersion

    // Check throttle (bypass if version field is missing/outdated)
    if (!needsVersion && !shouldWriteActivity(uid, lastWriteMs)) {
      log.debug('Activity write throttled', { uid })
      return
    }

    // Check if we need to promote status (only on first write opportunity)
    const promoteStatus = await shouldPromoteStatus()

    // Write activity (and optionally promote status in the same write)
    const result = await writeActivity(uid, { promoteStatus })

    if (result.success) {
      lastWriteMs = recordWriteTimestamp(uid)

      // If we promoted status, mark it in localStorage
      if (result.promoted) {
        markStatusPromoted(uid)
        log.info('User status promoted from "new" to "active"', { uid })
      }
    }
  }

  // Immediate write attempt on start
  attemptWrite()

  // Set up heartbeat
  heartbeatTimer = setInterval(() => {
    attemptWrite()
  }, HEARTBEAT_MS)

  // Return stop function
  return function stop() {
    if (stopped) return
    stopped = true

    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }

    log.info('Activity tracking stopped', { uid })
  }
}
